import {Component, OnDestroy, OnInit} from '@angular/core';
import {environment} from "../environments/environment";
import {OpenAPI} from "@esa/api";
import {TranslateService} from "@ngx-translate/core";
import {MsalBroadcastService, MsalService} from "@azure/msal-angular";
import {filter, Subject, takeUntil} from "rxjs";
import {EventMessage, EventType, InteractionStatus} from "@azure/msal-browser";
import {Event, NavigationEnd, Router} from "@angular/router";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title: string = 'ESAAMLG';
  isIframe: boolean = false;
  loginDisplay: boolean = false;
  currentOutlet: string = '';
  isAccessRestrictedRoute: boolean = false;
  delayedRender: boolean = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private msalAuthService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router
  ) {
    OpenAPI.BASE = environment.apiUrl;

    this.translateService.setDefaultLang('en-GB');
    this.translateService.use('en-GB');

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.currentOutlet = event?.url?.split('(')[1]?.split(')')[0];
        this.isAccessRestrictedRoute = event.urlAfterRedirects.split(/[?#]/)[0] === '/access-restricted';
        this.delayedRender = true;
      }
    });
  }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.setLoginDisplay();

    this.msalAuthService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      )
      .subscribe((result: EventMessage) => {
        if (this.msalAuthService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      })
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalAuthService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount(){
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalAuthService.instance.getActiveAccount();

    if (!activeAccount && this.msalAuthService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalAuthService.instance.getAllAccounts();
      this.msalAuthService.instance.setActiveAccount(accounts[0]);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
